import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    InputGroup,
    InputGroupText,
    Input,
    Label,
    Table,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Pagination,
    PaginationItem,
    PaginationLink,
    Spinner,
} from "reactstrap";
import { FiSearch, FiEye, FiTrash2, FiChevronDown } from "react-icons/fi";
import { getAllApplications, updateApplicationStatus, deleteApplication } from "../../services/api";
import { toast } from "react-toastify";
import "./Applicant.css";

const allStatuses = [
    "All",
    "New",
    "Under Review",
    "Shortlisted",
    "Interview Scheduled",
    "Selected",
    "Rejected",
];

// Helper: get initials from full name
const getInitials = (name = "") => {
    if (!name) return "??";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Helper: format date
const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
        return new Date(dateStr).toLocaleDateString("en-IN", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return dateStr;
    }
};

// Helper: map API response → UI shape
const mapApplication = (app) => {
    const skillsArray =
        typeof app.skills === "string"
            ? app.skills
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : Array.isArray(app.skills)
                ? app.skills
                : [];

    return {
        id: app.id,
        name: app.fullName || "Unknown",
        initials: getInitials(app.fullName),
        email: app.email || "-",
        phone: app.phone || "-",
        jobTitle: app.Job?.title || "N/A",
        experience: app.totalExperience || "-",
        appliedDate: formatDate(app.appliedAt || app.createdAt),
        status: app.status || "New",
        location: app.currentLocation || "-",
        skills: skillsArray,
        resumePath: app.resumePath || null,
        coverLetter: app.coverLetter || "",
        jobId: app.jobId,
    };
};

function Applicants() {
    const [applicants, setApplicants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [selectedApplicant, setSelectedApplicant] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: "" });

    const ITEMS_PER_PAGE = 5;

    // Fetch applications on mount
    const fetchApplications = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getAllApplications();
            const mapped = Array.isArray(data) ? data.map(mapApplication) : [];
            setApplicants(mapped);
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to load applications");
            setApplicants([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchApplications();
    }, []);

    // Filter
    const filteredApplicants = applicants.filter((app) => {
        const matchesSearch =
            app.name.toLowerCase().includes(search.toLowerCase()) ||
            app.jobTitle.toLowerCase().includes(search.toLowerCase()) ||
            app.email.toLowerCase().includes(search.toLowerCase());
        const matchesStatus = statusFilter === "All" || app.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    // Pagination
    const totalPages = Math.ceil(filteredApplicants.length / ITEMS_PER_PAGE) || 1;
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginated = filteredApplicants.slice(start, start + ITEMS_PER_PAGE);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [search, statusFilter]);

    const changePage = (page) => {
        if (page >= 1 && page <= totalPages) setCurrentPage(page);
    };

    const handleStatusChange = async (applicantId, newStatus) => {
        // Save previous status for rollback
        const previousStatus =
            applicants.find((a) => a.id === applicantId)?.status || "New";

        // Optimistic UI update
        setApplicants((prev) =>
            prev.map((a) =>
                a.id === applicantId ? { ...a, status: newStatus } : a
            )
        );
        setSelectedApplicant((prev) =>
            prev && prev.id === applicantId
                ? { ...prev, status: newStatus }
                : prev
        );

        try {
            await updateApplicationStatus(applicantId, newStatus);
            // Optional: show success toast if you use react-toastify
            // toast.success(`Status updated to "${newStatus}"`);
        } catch (err) {
            console.error(err);

            // Rollback on error
            setApplicants((prev) =>
                prev.map((a) =>
                    a.id === applicantId ? { ...a, status: previousStatus } : a
                )
            );
            setSelectedApplicant((prev) =>
                prev && prev.id === applicantId
                    ? { ...prev, status: previousStatus }
                    : prev
            );

            alert(err.message || "Failed to update status");
        }
    };

    // Delete
    const openDeleteModal = (id, name) => {
        setDeleteModal({ open: true, id, name });
    };

    const confirmDelete = async () => {
        if (!deleteModal.id) return;

        try {
            await deleteApplication(deleteModal.id);

            // Remove from UI
            setApplicants((prev) => prev.filter((a) => a.id !== deleteModal.id));

            // Close detail modal if the deleted one was open
            setSelectedApplicant((prev) =>
                prev && prev.id === deleteModal.id ? null : prev
            );

            setDeleteModal({ open: false, id: null, name: "" });

            toast.success("Applicant deleted successfully!", {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "light",
            });
        } catch (err) {
            console.error("Delete failed:", err);
            toast.error(err.message || "Failed to delete applicant", {
                position: "top-right",
                autoClose: 4000,
            });
        }
    };

    return (
        <div className="ap-page-wrapper">
            <Container fluid className="ap-container py-2 py-lg-3">
                {/* Header */}
                <div className="ap-header mb-2">
                    <h1 className="ap-title">Applicants</h1>
                    <p className="ap-subtitle">Review and manage all applications</p>
                </div>

                {/* Search + Filter */}
                <Row className="mb-5 align-items-center g-3">
                    <Col md={7} lg={6} xl={5}>
                        <InputGroup className="ap-search-group">
                            <InputGroupText className="ap-search-icon">
                                <FiSearch size={18} />
                            </InputGroupText>
                            <Input
                                placeholder="Search applicants..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="ap-search-input"
                            />
                        </InputGroup>
                    </Col>
                    <Col md="auto">
                        <UncontrolledDropdown>
                            <DropdownToggle
                                color="light"
                                outline
                                className="ap-status-filter d-flex align-items-center gap-2"
                            >
                                {statusFilter} <FiChevronDown size={16} />
                            </DropdownToggle>
                            <DropdownMenu>
                                {allStatuses.map((s) => (
                                    <DropdownItem
                                        key={s}
                                        active={statusFilter === s}
                                        onClick={() => setStatusFilter(s)}
                                    >
                                        {s}
                                    </DropdownItem>
                                ))}
                            </DropdownMenu>
                        </UncontrolledDropdown>
                    </Col>
                </Row>

                {/* Loading / Error */}
                {loading && (
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-3 text-muted">Loading applications...</p>
                    </div>
                )}

                {error && !loading && (
                    <div className="alert alert-danger d-flex justify-content-between align-items-center">
                        <span>{error}</span>
                        <Button color="danger" size="sm" outline onClick={fetchApplications}>
                            Retry
                        </Button>
                    </div>
                )}

                {/* Table */}
                {!loading && !error && (
                    <Card className="ap-table-card shadow border-0 overflow-hidden">
                        <CardBody className="p-0">
                            <div className="table-responsive">
                                <Table className="ap-applicants-table mb-0" hover borderless>
                                    <thead>
                                        <tr className="ap-table-header">
                                            <th>APPLICANT</th>
                                            <th>EMAIL</th>
                                            <th>PHONE</th>
                                            <th>JOB APPLIED FOR</th>
                                            <th>EXPERIENCE</th>
                                            <th>APPLIED DATE</th>
                                            <th>STATUS</th>
                                            <th>ACTIONS</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginated.map((app) => (
                                            <tr key={app.id} className="ap-table-row">
                                                <td>
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className="ap-avatar">{app.initials}</div>
                                                        <span className="ap-name">{app.name}</span>
                                                    </div>
                                                </td>
                                                <td className="ap-cell">{app.email}</td>
                                                <td className="ap-cell">{app.phone}</td>
                                                <td className="ap-cell ap-job-title">{app.jobTitle}</td>
                                                <td className="ap-cell">{app.experience}</td>
                                                <td className="ap-cell ap-date">{app.appliedDate}</td>
                                                <td>
                                                    <span
                                                        className={`ap-status-badge ap-status-${app.status
                                                            .toLowerCase()
                                                            .replace(/\s+/g, "-")}`}
                                                    >
                                                        {app.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="ap-actions">
                                                        <button
                                                            className="ap-action-btn ap-view-btn"
                                                            onClick={() => setSelectedApplicant(app)}
                                                            title="View Details"
                                                        >
                                                            <FiEye size={18} />
                                                        </button>
                                                        <button
                                                            className="ap-action-btn ap-delete-btn"
                                                            title="Delete Applicant"
                                                            onClick={() => openDeleteModal(app.id, app.name)}
                                                        >
                                                            <FiTrash2 size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>

                            {filteredApplicants.length === 0 && (
                                <div className="ap-no-results text-center py-5">
                                    No applicants found.
                                </div>
                            )}

                            {totalPages > 1 && (
                                <div className="ap-pagination p-4 d-flex justify-content-center">
                                    <Pagination size="sm">
                                        <PaginationItem disabled={currentPage === 1}>
                                            <PaginationLink
                                                previous
                                                onClick={() => changePage(currentPage - 1)}
                                            >
                                                Previous
                                            </PaginationLink>
                                        </PaginationItem>

                                        {[...Array(totalPages)].map((_, i) => {
                                            const page = i + 1;
                                            return (
                                                <PaginationItem active={page === currentPage} key={page}>
                                                    <PaginationLink onClick={() => changePage(page)}>
                                                        {page}
                                                    </PaginationLink>
                                                </PaginationItem>
                                            );
                                        })}

                                        <PaginationItem disabled={currentPage === totalPages}>
                                            <PaginationLink
                                                next
                                                onClick={() => changePage(currentPage + 1)}
                                            >
                                                Next
                                            </PaginationLink>
                                        </PaginationItem>
                                    </Pagination>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                )}

                {/* Applicant Detail Modal */}
                <Modal
                    isOpen={!!selectedApplicant}
                    toggle={() => setSelectedApplicant(null)}
                    centered
                    size="lg"
                    className="ap-detail-modal"
                >
                    <ModalHeader
                        toggle={() => setSelectedApplicant(null)}
                        className="ap-modal-header border-0"
                    >
                        Applicant Details
                    </ModalHeader>
                    <ModalBody className="ap-modal-body">
                        {selectedApplicant && (
                            <div className="ap-detail-content">
                                <div className="d-flex align-items-center gap-4 mb-4">
                                    <div className="ap-detail-avatar">
                                        {selectedApplicant.initials}
                                    </div>
                                    <div>
                                        <h4 className="ap-detail-name mb-1">
                                            {selectedApplicant.name}
                                        </h4>
                                        <p className="ap-detail-email mb-0">
                                            {selectedApplicant.email}
                                        </p>
                                    </div>
                                </div>

                                <Row className="g-3 mb-4">
                                    <Col md={6}>
                                        <div className="ap-detail-item">
                                            <span className="ap-detail-label">Phone:</span>
                                            <span>{selectedApplicant.phone}</span>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="ap-detail-item">
                                            <span className="ap-detail-label">Location:</span>
                                            <span>{selectedApplicant.location}</span>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="ap-detail-item">
                                            <span className="ap-detail-label">Experience:</span>
                                            <span>{selectedApplicant.experience}</span>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="ap-detail-item">
                                            <span className="ap-detail-label">Applied:</span>
                                            <span>{selectedApplicant.appliedDate}</span>
                                        </div>
                                    </Col>
                                </Row>

                                <div className="mb-4">
                                    <div className="ap-detail-item">
                                        <span className="ap-detail-label">Job Applied For:</span>
                                        <p className="ap-detail-job mb-0">
                                            {selectedApplicant.jobTitle}
                                        </p>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <div className="ap-detail-item">
                                        <span className="ap-detail-label d-block mb-2">Skills:</span>
                                        <div className="d-flex flex-wrap gap-2">
                                            {selectedApplicant.skills.length > 0 ? (
                                                selectedApplicant.skills.map((skill, idx) => (
                                                    <Badge key={idx} color="light" className="ap-skill-badge">
                                                        {skill}
                                                    </Badge>
                                                ))
                                            ) : (
                                                <span className="text-muted">No skills listed</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {selectedApplicant.coverLetter && (
                                    <div className="mb-4">
                                        <div className="ap-detail-item">
                                            <span className="ap-detail-label d-block mb-2">
                                                Cover Letter:
                                            </span>
                                            <p className="mb-0 text-muted" style={{ whiteSpace: "pre-wrap" }}>
                                                {selectedApplicant.coverLetter}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* {selectedApplicant.resumePath && (
                                    <div className="mb-4">
                                        <div className="ap-detail-item">
                                            <span className="ap-detail-label d-block mb-2">Resume:</span>
                                            <a
                                                href={`${process.env.REACT_APP_API_URL || ""}/uploads/${selectedApplicant.resumePath}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-sm btn-outline-primary"
                                            >
                                                Download Resume
                                            </a>
                                        </div>
                                    </div>
                                )} */}

                                <div>
                                    <Label className="ap-detail-label d-block mb-2">
                                        Update Status
                                    </Label>
                                    <UncontrolledDropdown>
                                        <DropdownToggle
                                            outline
                                            className="ap-status-dropdown w-100 text-start d-flex justify-content-between align-items-center"
                                        >
                                            <span>{selectedApplicant.status}</span>
                                            <FiChevronDown size={16} />
                                        </DropdownToggle>
                                        <DropdownMenu className="w-100">
                                            {[
                                                "New",
                                                "Under Review",
                                                "Shortlisted",
                                                "Interview Scheduled",
                                                "Selected",
                                                "Rejected",
                                            ].map((s) => (
                                                <DropdownItem
                                                    key={s}
                                                    onClick={() =>
                                                        handleStatusChange(selectedApplicant.id, s)
                                                    }
                                                >
                                                    {s}
                                                </DropdownItem>
                                            ))}
                                        </DropdownMenu>
                                    </UncontrolledDropdown>
                                </div>
                            </div>
                        )}
                    </ModalBody>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal
                    isOpen={deleteModal.open}
                    toggle={() => setDeleteModal({ open: false, id: null, name: "" })}
                    centered
                    className="ap-delete-modal"
                    backdrop="static"
                >
                    <ModalHeader
                        toggle={() => setDeleteModal({ open: false, id: null, name: "" })}
                        className="ap-modal-header border-0 pb-0"
                    >
                        <div className="d-flex align-items-center gap-3">
                            <div className="delete-icon-circle">
                                <FiTrash2 size={28} className="delete-icon" />
                            </div>
                            <h5 className="modal-title mb-0">Delete Applicant?</h5>
                        </div>
                    </ModalHeader>

                    <ModalBody className="ap-modal-body pt-3 pb-4">
                        <p className="text-muted mb-1">
                            You are about to permanently delete{" "}
                            <strong>{deleteModal.name}</strong>'s application.
                        </p>
                        <p className="fw-medium text-danger mb-0">
                            This action <strong>cannot be undone</strong>.
                        </p>
                    </ModalBody>

                    <ModalFooter className="ap-modal-footer border-0 pt-0 gap-3">
                        <Button
                            color="light"
                            outline
                            className="ap-cancel-btn px-4 py-2"
                            onClick={() =>
                                setDeleteModal({ open: false, id: null, name: "" })
                            }
                        >
                            Cancel
                        </Button>
                        <Button
                            color="danger"
                            className="ap-delete-confirm-btn px-4 py-2 d-flex align-items-center gap-2"
                            onClick={confirmDelete}
                        >
                            <FiTrash2 size={16} />
                            Delete Applicant
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    );
}

export default Applicants;